import { useCallback, useMemo } from "react";
import { useManualPaginatedResource } from "./useManualPaginatedResource";
import { DEFAULT_PAGE_SIZE } from "../constants/pagination";
import { TAB_PARAMS_PREFIX } from "../utils/sessionStorageKeys";

/**
 * Config for session-stored filters. One storage key per tab (independent state).
 */
export interface SessionStorageFiltersConfig<T extends Record<string, string>> {
  defaultFilters: T;
  filterKeys: readonly (keyof T)[];
  /** Tab identifier for sessionStorage key (e.g. "novedades", "banistas"). Each tab has its own key. */
  tabStorageId: string;
}

function getTabStorageKey(tabStorageId: string): string {
  return `${TAB_PARAMS_PREFIX}${tabStorageId}`;
}

function hasAnyFilter<T extends Record<string, string>>(
  filters: T,
  filterKeys: readonly (keyof T)[]
): boolean {
  return filterKeys.some((k) => (filters[k] ?? "") !== "");
}

/**
 * useSessionStorageManualPaginatedResource
 *
 * Manual pagination + filters persisted in sessionStorage only (no URL).
 * Use tabStorageId so each tab (e.g. novedades, banistas) has independent state.
 * Cleared on login/logout via clearAppSessionStorage().
 */
export function useSessionStorageManualPaginatedResource<
  TFilters extends Record<string, string>
>(config: SessionStorageFiltersConfig<TFilters>) {
  const { defaultFilters, filterKeys, tabStorageId } = config;
  const storageKey = getTabStorageKey(tabStorageId);

  const initialFromStorage = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const saved = JSON.parse(raw) as {
        filters?: TFilters;
        page?: number;
        pageSize?: number;
      };
      if (
        saved &&
        typeof saved.page === "number" &&
        typeof saved.pageSize === "number" &&
        saved.filters &&
        typeof saved.filters === "object"
      ) {
        const page = Math.max(1, saved.page);
        const pageSize = Math.max(
          1,
          Math.min(100, saved.pageSize ?? DEFAULT_PAGE_SIZE)
        );
        return {
          filters: hasAnyFilter(saved.filters, filterKeys)
            ? saved.filters
            : undefined,
          page,
          pageSize,
        };
      }
    } catch {
      // ignore parse errors
    }
    return null;
    // Intentionally only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialFilters = initialFromStorage?.filters;
  const initialPage = initialFromStorage?.page ?? 1;
  const initialPageSize = initialFromStorage?.pageSize ?? DEFAULT_PAGE_SIZE;

  const {
    page,
    pageSize,
    draftFilters,
    appliedFilters,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    applyFilters,
    resetFilters,
  } = useManualPaginatedResource({
    initialFilters: defaultFilters,
    initialAppliedFilters: initialFilters,
    initialPageSize,
    initialPage,
  });

  const saveToStorage = useCallback(
    (state: { filters: TFilters; page: number; pageSize: number }) => {
      try {
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            filters: state.filters,
            page: state.page,
            pageSize: state.pageSize,
          })
        );
      } catch {
        // ignore storage errors
      }
    },
    [storageKey]
  );

  const applyFiltersAndSave = useCallback(() => {
    applyFilters();
    saveToStorage({ filters: draftFilters, page: 1, pageSize });
  }, [applyFilters, draftFilters, pageSize, saveToStorage]);

  const resetFiltersAndSave = useCallback(() => {
    resetFilters();
    saveToStorage({ filters: defaultFilters, page: 1, pageSize });
  }, [resetFilters, pageSize, saveToStorage, defaultFilters]);

  const handlePageChangeAndSave = useCallback(
    (newPage: number) => {
      handlePageChange(newPage);
      saveToStorage({ filters: appliedFilters, page: newPage, pageSize });
    },
    [handlePageChange, saveToStorage, appliedFilters, pageSize]
  );

  const handlePageSizeChangeAndSave = useCallback(
    (newSize: number) => {
      handlePageSizeChange(newSize);
      saveToStorage({
        filters: appliedFilters,
        page: 1,
        pageSize: newSize,
      });
    },
    [handlePageSizeChange, saveToStorage, appliedFilters]
  );

  return {
    page,
    pageSize,
    draftFilters,
    appliedFilters,
    handlePageChange: handlePageChangeAndSave,
    handlePageSizeChange: handlePageSizeChangeAndSave,
    handleFilterChange,
    applyFilters: applyFiltersAndSave,
    resetFilters: resetFiltersAndSave,
  };
}
